import { Col, Row } from 'antd';
import { Form, Formik } from 'formik';
import { Address } from 'models/Address/address.model';
import React, { FC, FocusEventHandler, useState, useEffect } from 'react';
import OrganizationModal from 'shared/components/OrganizationModal';
import Map from 'shared/components/map';
import fetchAddressFromGeoCode from 'shared/utils/fetchAddressFromGeoCode';
import styles from './locationModal.module.scss';
import { fetchGeocodeData } from 'shared/utils/fetchGeoLocation';
import { locationValidationSchema } from './locationValidationSchema';
import FieldsRow from 'shared/components/FieldsRow';
import { getLocationDataLayout } from './locationLayoutData';

interface LocationModalProps {
  showModal: boolean;
  onCancel: () => void;
  title?: string;
  initialValue?: Address | string; // Accept both Address object and string address
  handleSubmit: (address: Address) => void;
}

export const generateAddressLabel = (address: Address): string => {
  const addressParts: string[] = [];

  // Add street address if available
  if (address.street?.trim()) {
    addressParts.push(address.street.trim());
  }

  // Add city if available
  if (address.city?.trim()) {
    addressParts.push(address.city.trim());
  }

  // Add province if available
  if (address.province?.trim()) {
    addressParts.push(address.province.trim());
  }

  // Add postal code if available
  if (address.postalCode?.trim()) {
    addressParts.push(address.postalCode.trim());
  }

  // Add country if available
  if (address.country?.trim()) {
    addressParts.push(address.country.trim());
  }

  // Join all parts with comma and space, or return empty string if no parts
  return addressParts.length > 0 ? addressParts.join(', ') : '';
};

const LocationModal: FC<LocationModalProps> = props => {
  const { showModal, onCancel, title, initialValue, handleSubmit } = props;
  const [fetchingAddress, setFetchingAddress] = useState<boolean>(false);

  const setAddressInState = (
    addressComponents: google.maps.GeocoderAddressComponent[],
    latling: google.maps.LatLng | null,
    setFieldValue: Function
  ) => {
    const { streetLocality, country, province, city, pinCode } =
      fetchAddressFromGeoCode(addressComponents);

    if (streetLocality.trim() === '') {
      setFieldValue('street', '');
    } else {
      setFieldValue('street', streetLocality?.trim() ?? '');
    }
    setFieldValue('country', country?.long_name ?? '');
    setFieldValue('countryCode', country?.short_name ?? '');
    setFieldValue('province', province?.long_name ?? '');
    setFieldValue('provinceCode', province?.short_name ?? '');
    setFieldValue('city', city?.long_name ?? '');
    setFieldValue('postalCode', pinCode?.long_name ?? '');
    setFieldValue('latitude', latling?.lat());
    setFieldValue('longitude', latling?.lng());
  };
  const searchPlaceByCountryProvince = async (
    country: string,
    province: string,
    setFieldValue: Function
  ) => {
    try {
      setFetchingAddress(true);
      const results = await fetchGeocodeData(`${province} ${country}`);
      if (results) {
        setFieldValue('map', results?.formattedAddress);
        setAddressInState(
          results?.addressComponents,
          results?.latLng,
          setFieldValue
        );
      }
    } catch (error) {
    } finally {
      setFetchingAddress(false);
    }
  };

  const locationModalFooterProps = (submitForm: () => Promise<void>) => {
    return {
      confirmButtonProps: {
        children: 'Submit',
        primary: true,
        loading: fetchingAddress,
        onClick: () => {
          submitForm();
        },
      },
      cancelButtonProps: {
        children: 'Cancel',
        secondary: true,
        disabled: fetchingAddress,
        onClick: onCancel,
      },
      wrapClassName: styles.footerWrapper,
    };
  };

  const handleOnBlur =
    (
      values: Address,
      setFieldValue: (
        field: string,
        value: string,
        shouldValidate?: boolean
      ) => void
    ): FocusEventHandler =>
      e => {
        if (values.country && values?.province)
          searchPlaceByCountryProvince(
            values.country ?? '',
            values.province ?? '',
            setFieldValue
          );
      };

  const setAddress =
    (
      setFieldValue: (
        field: string,
        value: string,
        shouldValidate?: boolean
      ) => void
    ) =>
      (
        addressComponents: google.maps.GeocoderAddressComponent[],
        latling: google.maps.LatLng | null
      ) =>
        setAddressInState(addressComponents, latling, setFieldValue);

  // Handle case where initialValue might be a string (from Excel upload)
  // Convert string to Address object with map field populated for geocoding
  const getInitialAddress = (): Address => {
    if (!initialValue) {
      return new Address();
    }

    // If it's already an Address object, return it
    if (initialValue instanceof Address || (typeof initialValue === 'object' && initialValue !== null && 'street' in initialValue)) {
      return initialValue as Address;
    }

    // If it's a string, create Address object with map field populated
    // This allows the Map component to geocode the string when the modal opens
    if (typeof initialValue === 'string' && initialValue.trim() !== '') {
      const address = new Address();
      address.map = initialValue.trim();
      return address;
    }

    return new Address();
  };

  return (
    <Formik
      initialValues={getInitialAddress()}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={locationValidationSchema}>
      {({ values, setFieldValue, submitForm }) => {
        return (
          <OrganizationModal
            visible={showModal}
            onClose={onCancel}
            width={725}
            title={title ?? 'Add Location'}
            modalFooterProps={locationModalFooterProps(submitForm)}
            wrapClassName={styles.modalWrapper}
            children={
              <Form>
                <Row gutter={[20, 25]}>
                  <Col span={24}>
                    <Map
                      name="map"
                      setAddress={setAddress(setFieldValue)}
                      latitude={values?.latitude}
                      longitude={values?.longitude}
                    />
                  </Col>
                  <Col span={24}>
                    <FieldsRow
                      {...getLocationDataLayout(
                        handleOnBlur(values, setFieldValue)
                      )}
                      rowClassName={styles.rowWrap}
                    />
                  </Col>
                </Row>
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};
export default LocationModal;
